import { useError } from "@/contexts/error";
import AuthService from "@/services/auth-service";
import { useMsal } from "@azure/msal-react";
import { ResponseError } from "../openapi";
import { ApiMultipleResult, ApiResult } from "../query.schemas";
import { getMessageHeaders } from "@/utils/utils";

type CustomFetchClient<T> = (data: {
    url: string;
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    params?: Record<string, any>; // Here we are coercing the type of the params from Record<string, any> to Record<string, string> for creating the URLSearchParams.
    headers?: Record<string, string>;
    data?: BodyType<unknown> | FormData;
    responseType?: string;
    signal?: AbortSignal;
}) => Promise<T>;

const basePath =
    (import.meta.env.VITE_ILLUM_API as string | undefined) ??
    "https://illuminatewebdev.jetcharge.com.au/api";

export const useCustomFetchClient = <T>(): CustomFetchClient<T> => {
    const { instance } = useMsal();
    const errorContext = useError();

    return async ({ url, method, params, data, headers: callerHeaders }) => {
        const fetchUrl = new URL(
            url.startsWith("/") ? `.${url}` : url,
            basePath.endsWith("/") ? basePath : `${basePath}/`,
        );
        // URLSearchParams stringifies Dates in non-ISO local-time form
        // ("Wed Apr 01 2025 ... GMT+1100 (...)"), which the API can't parse —
        // convert them to ISO 8601 first.
        fetchUrl.search = new URLSearchParams(
            params &&
                Object.fromEntries(
                    Object.entries(params).map(([key, value]) => [
                        key,
                        value instanceof Date ? value.toISOString() : value,
                    ]),
                ),
        ).toString();

        const isFormDataBody = data instanceof FormData;

        const headers: HeadersInit = {
            ...(!isFormDataBody && data?.headers ? data.headers : {}),
            ...getMessageHeaders(),
            ...callerHeaders,
            Authorization: !import.meta.env["STORYBOOK"]
                ? await AuthService.getAuthorizationHeader(instance)
                : "",
        };

        const response = await fetch(fetchUrl, {
            method,
            headers,
            ...(data
                ? { body: isFormDataBody ? data : JSON.stringify(data) }
                : {}),
        });

        if (response.ok) {
            // A 204 No Content response should return null because it has no body.
            if (response.status === 204) {
                return null as T;
            }

            // This check must be below because the 204 No Content response has no content-type header.
            if (
                !response.headers
                    .get("content-type")
                    ?.includes("application/json")
            ) {
                return response;
            }

            const responseJSON = await response.json();

            if (!responseJSON.errorNo) {
                return responseJSON;
            }

            if (responseJSON.extras) {
                throw new ApiMultipleResultError(responseJSON);
            }

            throw new ApiResultError(responseJSON);
        }

        errorContext.setStatusCode(response.status);

        throw new ResponseError(response);
    };
};
export const isInstanceOfApiResultError = (
    error: unknown,
): error is ApiResultError => {
    return (error as ApiResultError).name === "ApiResultError";
};
export class ApiResultError extends Error {
    override name: "ApiResultError" = "ApiResultError";
    public errorNo?: number;
    constructor(public result: ApiResult) {
        super(result.message);
        this.errorNo = result.errorNo;
    }
}
export class ApiMultipleResultError extends Error {
    override name: "ApiMultipleResultError" = "ApiMultipleResultError";
    constructor(public result: ApiMultipleResult) {
        super(result.message);
    }
}
export default useCustomFetchClient;

export type ErrorType<ErrorData> = ErrorData;

export type BodyType<BodyData> = BodyData & { headers?: any };
